import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart, BarChart, Bar, Cell, ResponsiveContainer, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, LabelList
} from 'recharts';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Target,
  BookOpen, Calendar, Hash, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, Eye, EyeOff,
  HelpCircle, MousePointerClick, BarChart3,
  Layers, RefreshCcw, Lightbulb, ArrowUpRight, ArrowDownRight, ArrowRight,
  Zap, Shield, AlertCircle, Activity, Flame,
  Trophy, Award, Grid3X3, Info
} from 'lucide-react';
import config from '../config';

const AREA_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7', '#F43F5E', '#22C55E', '#0EA5E9'
];

function ProgressDashboard({ darkMode = false, onBack }) {
  const [view, setView] = useState('overview');
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [areaDetailData, setAreaDetailData] = useState(null);
  const [topicTrendData, setTopicTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnevaluated, setShowUnevaluated] = useState(false);
  const [predictionsData, setPredictionsData] = useState(null);
  const [lastSimulacro, setLastSimulacro] = useState(null);
  const [subtopicDetailData, setSubtopicDetailData] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [overviewTab, setOverviewTab] = useState('resumen');

  const apiUrl = config.getApiUrl();

  // ---------- helpers ----------
  const n = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: 'bg-emerald-500', light: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Dominado' };
    if (score >= 50) return { bg: 'bg-amber-500', light: 'bg-amber-500/20', text: 'text-amber-400', label: 'En progreso' };
    return { bg: 'bg-rose-500', light: 'bg-rose-500/20', text: 'text-rose-400', label: 'Reforzar' };
  };
  // Backward compat alias
  const getAccuracyColor = getScoreColor;

  // Premium StatCard (matching YearAnalysisDashboard pattern)
  const StatCard = ({ title, value, subtitle, icon: Icon, accentColor = 'blue', children }) => {
    const colorClasses = {
      blue: darkMode ? 'from-blue-500/20 to-cyan-500/20 border-blue-500/30' : 'from-blue-50 to-cyan-50 border-blue-200',
      green: darkMode ? 'from-emerald-500/20 to-green-500/20 border-emerald-500/30' : 'from-emerald-50 to-green-50 border-emerald-200',
      amber: darkMode ? 'from-amber-500/20 to-orange-500/20 border-amber-500/30' : 'from-amber-50 to-orange-50 border-amber-200',
      purple: darkMode ? 'from-purple-500/20 to-pink-500/20 border-purple-500/30' : 'from-purple-50 to-pink-50 border-purple-200',
      rose: darkMode ? 'from-rose-500/20 to-pink-500/20 border-rose-500/30' : 'from-rose-50 to-pink-50 border-rose-200',
    };
    const iconColors = {
      blue: darkMode ? 'text-blue-400' : 'text-blue-600',
      green: darkMode ? 'text-emerald-400' : 'text-emerald-600',
      amber: darkMode ? 'text-amber-400' : 'text-amber-600',
      purple: darkMode ? 'text-purple-400' : 'text-purple-600',
      rose: darkMode ? 'text-rose-400' : 'text-rose-600',
    };
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${colorClasses[accentColor]} p-5 ${
          darkMode ? 'bg-slate-900/60' : 'bg-white'
        } shadow-lg`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{title}</p>
            <p className={`mt-2 text-2xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-800'}`} title={typeof value === 'string' ? value : undefined}>{value}</p>
            {subtitle && <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{subtitle}</p>}
          </div>
          <div className={`rounded-xl p-3 ${darkMode ? 'bg-slate-800/50' : 'bg-white/80'} shadow-inner`}>
            <Icon className={`h-6 w-6 ${iconColors[accentColor]}`} />
          </div>
        </div>
        {children}
      </motion.div>
    );
  };

  const CompositeIndicators = ({ composite, total, bankCount, dm }) => {
    if (!composite) return null;
    const bar = (label, value, color) => (
      <div className="flex items-center gap-2">
        <span className={`text-xs w-20 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{label}</span>
        <div className={`flex-1 h-1.5 rounded-full ${dm ? 'bg-slate-700' : 'bg-gray-200'}`}>
          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
        </div>
        <span className={`text-xs font-medium w-8 text-right ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{value}%</span>
      </div>
    );
    return (
      <div className="space-y-1.5 mt-3">
        {bar('Confianza', composite.confidence, 'bg-blue-500')}
        {bar('Cobertura', composite.coverage, 'bg-violet-500')}
        {bar('Vigencia', composite.recency, 'bg-cyan-500')}
      </div>
    );
  };

  // Premium bar chart with unique gradient per bar, glass tooltip, and reference zones
  const ScoreBarChart = ({ data, height = 450, onBarClick }) => {
    const needsAngle = data.length > 5 || data.some(d => (d.name || '').length > 12);
    const bottomMargin = needsAngle ? 100 : 40;
    const chartMinWidth = Math.max(data.length * 72, 500);
    const needsScroll = data.length > 10;

    // Rich 3D gradient pairs: bright highlight on top, deep saturated on bottom
    const GRADIENT_PALETTES = [
      { top: '#60A5FA', mid: '#3B82F6', bottom: '#1D4ED8' }, // blue
      { top: '#34D399', mid: '#10B981', bottom: '#047857' }, // emerald
      { top: '#FBBF24', mid: '#F59E0B', bottom: '#B45309' }, // amber
      { top: '#F87171', mid: '#EF4444', bottom: '#B91C1C' }, // red
      { top: '#A78BFA', mid: '#8B5CF6', bottom: '#6D28D9' }, // violet
      { top: '#F472B6', mid: '#EC4899', bottom: '#BE185D' }, // pink
      { top: '#22D3EE', mid: '#06B6D4', bottom: '#0E7490' }, // cyan
      { top: '#A3E635', mid: '#84CC16', bottom: '#4D7C0F' }, // lime
      { top: '#FB923C', mid: '#F97316', bottom: '#C2410C' }, // orange
      { top: '#818CF8', mid: '#6366F1', bottom: '#4338CA' }, // indigo
      { top: '#2DD4BF', mid: '#14B8A6', bottom: '#0F766E' }, // teal
      { top: '#C084FC', mid: '#A855F7', bottom: '#7E22CE' }, // purple
      { top: '#FB7185', mid: '#F43F5E', bottom: '#BE123C' }, // rose
      { top: '#4ADE80', mid: '#22C55E', bottom: '#15803D' }, // green
      { top: '#38BDF8', mid: '#0EA5E9', bottom: '#0369A1' }, // sky
    ];

    const BarTooltip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const d = payload[0].payload;
      const palette = GRADIENT_PALETTES[d._colorIndex % GRADIENT_PALETTES.length];
      const barColor = palette.mid;
      const statusLabel = d.score >= 80 ? 'Dominado' : d.score >= 50 ? 'En progreso' : 'Reforzar';
      const statusIcon = d.score >= 80 ? '🏆' : d.score >= 50 ? '📈' : '🔻';
      const statusBg = d.score >= 80 ? '#10b981' : d.score >= 50 ? '#f59e0b' : '#ef4444';
      return (
        <div className={`px-5 py-4 rounded-2xl shadow-2xl border-2 ${
          darkMode ? 'bg-slate-900/95 border-slate-500/30' : 'bg-white/98 border-gray-100'
        }`} style={{ backdropFilter: 'blur(20px)', minWidth: 200,
          boxShadow: `0 8px 32px ${barColor}25, 0 4px 16px rgba(0,0,0,0.15)` }}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-4 h-4 rounded-md shadow-inner" style={{
              background: `linear-gradient(135deg, ${palette.top}, ${palette.bottom})`
            }} />
            <p className={`font-bold text-sm ${textPrimary}`}>{d.fullName || d.name}</p>
          </div>
          <div className="flex items-center justify-between gap-4 mb-3">
            <span className="text-3xl font-black tracking-tight" style={{
              background: `linear-gradient(135deg, ${palette.top}, ${palette.bottom})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>{d.score}%</span>
            <span className="text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
              style={{ backgroundColor: statusBg + '18', color: statusBg, border: `1px solid ${statusBg}30` }}>
              {statusIcon} {statusLabel}
            </span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700/80' : 'bg-gray-100'}`}>
            <div className="h-full rounded-full transition-all" style={{
              width: `${Math.min(d.score, 100)}%`,
              background: `linear-gradient(90deg, ${palette.top}, ${palette.mid}, ${palette.bottom})`
            }} />
          </div>
          {d.total && (
            <p className={`mt-2 text-xs ${textMuted}`}>
              {d.correct || 0}/{d.total} correctas · {d.sessions || '?'} sesiones
            </p>
          )}
        </div>
      );
    };

    // Enrich data with color index
    const enrichedData = data.map((d, i) => ({ ...d, _colorIndex: i }));

    // Custom label renderer with status indicator
    const renderCustomLabel = (props) => {
      const { x, y, width, value, index } = props;
      const entry = enrichedData[index];
      if (!entry) return null;
      const palette = GRADIENT_PALETTES[index % GRADIENT_PALETTES.length];
      return (
        <g>
          <text x={x + width / 2} y={y - 8} textAnchor="middle"
            style={{ fontSize: 12, fontWeight: 800, fill: darkMode ? '#f1f5f9' : '#1e293b' }}>
            {value}%
          </text>
          {value >= 80 && (
            <text x={x + width / 2} y={y - 22} textAnchor="middle" style={{ fontSize: 10 }}>✓</text>
          )}
          {value < 40 && (
            <text x={x + width / 2} y={y - 22} textAnchor="middle" style={{ fontSize: 10 }}>!</text>
          )}
        </g>
      );
    };

    const chart = (
      <ResponsiveContainer width="100%" height={Math.max(height, 380)}>
        <BarChart data={enrichedData} margin={{ top: 40, right: 15, left: -5, bottom: bottomMargin }}
          onClick={onBarClick ? (state) => { if (state?.activePayload?.[0]) onBarClick(state.activePayload[0].payload); } : undefined}
          style={onBarClick ? { cursor: 'pointer' } : undefined}>
          <defs>
            {/* 3D gradient for each bar */}
            {GRADIENT_PALETTES.map((pal, i) => (
              <linearGradient key={`barG3d_${i}`} id={`barG3d_${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={pal.top} stopOpacity={0.95} />
                <stop offset="45%" stopColor={pal.mid} stopOpacity={1} />
                <stop offset="100%" stopColor={pal.bottom} stopOpacity={0.9} />
              </linearGradient>
            ))}
            {/* Glow filter for high-scoring bars */}
            <filter id="glowHigh" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {/* Subtle stripe pattern for danger zone */}
            <pattern id="dangerStripes" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="2" height="6" fill={darkMode ? '#ef444408' : '#ef444406'} />
            </pattern>
          </defs>

          <CartesianGrid strokeDasharray="3 3" vertical={false}
            stroke={darkMode ? '#1e293b' : '#f1f5f9'} />

          {/* Danger zone: red tinted area below 50% */}
          <ReferenceArea y1={0} y2={50}
            fill={darkMode ? '#ef444410' : '#fef2f208'}
            fillOpacity={1} />

          {/* Excellence zone: green tinted area above 80% */}
          <ReferenceArea y1={80} y2={100}
            fill={darkMode ? '#10b98108' : '#f0fdf406'}
            fillOpacity={1} />

          <XAxis dataKey="name" stroke={darkMode ? '#475569' : '#94a3b8'}
            tick={{ fontSize: 11, fill: darkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}
            angle={needsAngle ? -40 : 0} textAnchor={needsAngle ? 'end' : 'middle'}
            interval={0} axisLine={{ stroke: darkMode ? '#334155' : '#e2e8f0' }} tickLine={false} />
          <YAxis domain={[0, 100]} stroke={darkMode ? '#475569' : '#94a3b8'}
            tick={{ fontSize: 11, fill: darkMode ? '#94a3b8' : '#64748b' }}
            tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false}
            ticks={[0, 25, 50, 80, 100]} />
          <Tooltip content={<BarTooltip />}
            cursor={{ fill: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', radius: 8 }} />

          {/* Reference lines */}
          <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="8 4" strokeWidth={2}
            label={{ value: '🎯 Meta 80%', fill: '#22c55e', fontSize: 11, fontWeight: 700, position: 'insideTopRight' }} />
          <ReferenceLine y={50} stroke={darkMode ? '#f59e0b66' : '#f59e0b55'} strokeDasharray="5 5" strokeWidth={1.5}
            label={{ value: '⚠ 50%', fill: darkMode ? '#f59e0b99' : '#f59e0bbb', fontSize: 10, fontWeight: 600, position: 'insideBottomRight' }} />

          <Bar dataKey="score" radius={[12, 12, 0, 0]} maxBarSize={58}
            animationDuration={1200} animationEasing="ease-out">
            {enrichedData.map((entry, i) => (
              <Cell key={i}
                fill={`url(#barG3d_${i % GRADIENT_PALETTES.length})`}
                style={entry.score >= 80 ? { filter: 'url(#glowHigh)' } : undefined} />
            ))}
            <LabelList content={renderCustomLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );

    if (needsScroll) {
      return (
        <div className="overflow-x-auto rounded-xl -mx-2 px-2 pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div style={{ minWidth: chartMinWidth }}>{chart}</div>
        </div>
      );
    }
    return chart;
  };

  // Premium breakdown: vertical chart + interactive detail list for subtopics/specifics
  const PremiumBreakdown = ({ items, title, subtitle, onItemClick, emptyMessage = 'Sin datos suficientes' }) => {
    if (!items || items.length === 0) {
      return (
        <div className={cardBase}>
          <div className="h-[160px] flex flex-col items-center justify-center gap-3">
            <BarChart3 className={`w-10 h-10 ${textMuted}`} />
            <p className={textSecondary}>{emptyMessage}</p>
          </div>
        </div>
      );
    }

    const avgScore = Math.round(items.reduce((s, i) => s + i.score, 0) / items.length);
    const weakCount = items.filter(i => i.score < 50).length;
    const masteredCount = items.filter(i => i.score >= 80).length;
    const isClickable = !!onItemClick;

    const chartItems = items.map(item => ({
      ...item,
      name: (item.name || '').length > 18 ? item.name.substring(0, 17) + '\u2026' : item.name,
    }));

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
        {/* Header with summary pills */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h2 className={`text-lg font-semibold ${textPrimary}`}>{title}</h2>
            {subtitle && <p className={`text-xs ${textMuted} mt-0.5`}>{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-slate-600'}`}>
              <Activity className="w-3 h-3" /> Promedio: {avgScore}%
            </span>
            {weakCount > 0 && (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${darkMode ? 'bg-rose-500/15 text-rose-400' : 'bg-rose-50 text-rose-600'}`}>
                <Flame className="w-3 h-3" /> {weakCount} reforzar
              </span>
            )}
            {masteredCount > 0 && (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${darkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                <Shield className="w-3 h-3" /> {masteredCount} dominados
              </span>
            )}
          </div>
        </div>

        {/* Premium Vertical Bar Chart */}
        {items.length >= 2 && (
          <div className={`${darkMode ? 'bg-gradient-to-b from-slate-800 to-slate-800/80 border-slate-700/60' : 'bg-gradient-to-b from-white to-gray-50/50 border-gray-200/70'} border rounded-2xl p-5 shadow-lg`}>
            <ScoreBarChart data={chartItems} height={items.length <= 5 ? 300 : 380}
              onBarClick={isClickable ? (payload) => onItemClick(payload) : undefined} />
          </div>
        )}

        {/* Detail rows */}
        <div className={`${darkMode ? 'bg-slate-800/90 border-slate-700/50' : 'bg-white border-gray-200/70'} border rounded-2xl shadow-lg overflow-hidden`}>
          {/* Legend */}
          <div className={`flex items-center justify-between px-5 py-2.5 border-b ${darkMode ? 'border-slate-700/50 bg-slate-900/30' : 'border-gray-100 bg-gray-50/70'}`}>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-gradient-to-b from-rose-400 to-rose-600" /><span className={`text-[11px] ${textMuted}`}>{'<'}50%</span></span>
              <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-gradient-to-b from-amber-400 to-amber-600" /><span className={`text-[11px] ${textMuted}`}>50-79%</span></span>
              <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-gradient-to-b from-emerald-400 to-emerald-600" /><span className={`text-[11px] ${textMuted}`}>≥80%</span></span>
            </div>
            <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-500">
              <Target className="w-3 h-3" /> Meta 80%
            </span>
          </div>

          {/* Rows */}
          <div className={`divide-y ${darkMode ? 'divide-slate-700/40' : 'divide-gray-100'}`}>
            {items.map((item, i) => (
              <div key={item.id || item.name}
                className={`group px-5 py-3.5 transition-all duration-200 hover:scale-[1.003] ${
                  isClickable ? 'cursor-pointer' : ''
                } ${
                  item.score < 50
                    ? darkMode ? 'hover:bg-rose-500/5' : 'hover:bg-rose-50/40'
                    : darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50/80'
                }`}
                onClick={isClickable ? () => onItemClick(item) : undefined}
              >
                <div className="flex items-center gap-3 md:gap-4">
                  {/* Position badge */}
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    i === 0 ? 'bg-amber-400 text-amber-900' :
                    i === 1 ? 'bg-slate-300 text-slate-700' :
                    i === 2 ? 'bg-amber-600 text-white' :
                    darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                  }`}>{i + 1}</span>
                  {/* Name */}
                  <div className="w-[120px] sm:w-[150px] md:w-[190px] shrink-0">
                    <div className="flex items-center gap-1.5">
                      {item.score < 50 && <Flame className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                      {item.score >= 80 && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                      {item.score >= 50 && item.score < 80 && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                      <span className={`text-sm font-medium ${textPrimary} truncate`} title={item.fullName || item.name}>{item.name}</span>
                    </div>
                    <span className={`text-[11px] ${textMuted}`}>{item.correct}/{item.total} correctas</span>
                  </div>

                  {/* Animated horizontal bar */}
                  <div className="flex-1 relative min-w-0">
                    <div className={`h-8 rounded-lg overflow-hidden relative ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                      <div className="absolute top-0 bottom-0 z-10 border-l-2 border-dashed" style={{ left: '80%', borderColor: darkMode ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.35)' }} />
                      <motion.div initial={{ width: 0 }}
                        animate={{ width: `${Math.min(item.score, 100)}%` }}
                        transition={{ delay: 0.08 + i * 0.04, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full rounded-lg relative overflow-hidden"
                        style={{ backgroundColor: AREA_COLORS[i % AREA_COLORS.length] }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                      </motion.div>
                      <div className="absolute inset-0 flex items-center pointer-events-none">
                        <span className={`text-xs font-bold drop-shadow-sm ${
                          item.score > 18 ? 'text-white ml-2' : darkMode ? 'text-slate-300' : 'text-slate-600'
                        }`} style={item.score <= 18 ? { marginLeft: `calc(${Math.min(item.score, 100)}% + 6px)` } : {}}>
                          {item.score}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Trend + Badge + Arrow */}
                  <div className="flex items-center gap-2 shrink-0">
                    {getTrendIcon(item.trend)}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full hidden sm:inline-block ${item.color.light} ${item.color.text}`}>
                      {item.color.label}
                    </span>
                    {isClickable && <ArrowRight className={`w-4 h-4 ${textMuted} opacity-0 group-hover:opacity-100 transition-opacity`} />}
                  </div>
                </div>

                {/* Compact composite */}
                {item.composite && (
                  <div className="flex items-center gap-3 md:gap-5 mt-2 md:ml-[216px]">
                    {[
                      { label: 'Confianza', value: item.composite.confidence, color: 'bg-blue-500' },
                      { label: 'Cobertura', value: item.composite.coverage, color: 'bg-violet-500' },
                      { label: 'Vigencia', value: item.composite.recency, color: 'bg-cyan-500' },
                    ].map(ind => (
                      <div key={ind.label} className="flex items-center gap-1.5 flex-1 max-w-[150px]">
                        <span className={`text-[10px] ${textMuted} w-[50px] shrink-0`}>{ind.label}</span>
                        <div className={`flex-1 h-1 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div className={`h-full rounded-full ${ind.color}`} style={{ width: `${Math.min(ind.value, 100)}%` }} />
                        </div>
                        <span className={`text-[10px] font-medium ${textMuted} w-6 text-right`}>{ind.value}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-rose-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getDiagnostic = (accuracy, composite, trend) => {
    const acc = n(accuracy);
    const conf = composite?.confidence || 0;
    const cov = composite?.coverage || 0;
    const vig = composite?.recency || 0;
    const score = composite?.score || 0;

    // Dominado con buena evidencia
    if (score >= 80 && conf >= 70 && vig >= 60)
      return { icon: '✓', text: 'Dominado. Manten con repaso periodico.', color: 'emerald' };

    // Mejorando
    if (trend === 'up' && score < 80)
      return { icon: '↑', text: 'Estas mejorando. Sigue asi.', color: 'blue' };

    // Empeorando
    if (trend === 'down')
      return { icon: '↓', text: 'Tu rendimiento esta bajando. Repasa este tema.', color: 'rose' };

    // Debil con poca evidencia
    if (acc < 50 && conf < 60)
      return { icon: '?', text: 'Area debil con poca evidencia. Responde mas preguntas para confirmar tu nivel.', color: 'amber' };

    // Debil confirmado
    if (acc < 50 && conf >= 60)
      return { icon: '!', text: 'Area debil confirmada. Prioriza estudiar este tema.', color: 'rose' };

    // Buen accuracy pero poca cobertura
    if (acc >= 60 && cov < 30)
      return { icon: '◐', text: 'Vas bien pero solo viste una fraccion del banco. Cubre mas preguntas.', color: 'violet' };

    // Buen accuracy pero vigencia baja
    if (acc >= 60 && vig < 50)
      return { icon: '⏳', text: 'Buen nivel pero necesitas repasar. Hace tiempo que no practicas.', color: 'cyan' };

    // En progreso general
    if (score >= 50 && score < 80)
      return { icon: '→', text: 'En progreso. Sigue practicando para llegar al 80%.', color: 'amber' };

    // Fallback
    return { icon: '•', text: 'Necesitas mas practica en este tema.', color: 'slate' };
  };

  const DiagnosticBadge = ({ accuracy, composite, trend, dm }) => {
    const d = getDiagnostic(accuracy, composite, trend);
    const colors = {
      emerald: dm ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-700',
      blue: dm ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-700',
      rose: dm ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' : 'bg-rose-50 border-rose-200 text-rose-700',
      amber: dm ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700',
      violet: dm ? 'bg-violet-500/10 border-violet-500/20 text-violet-400' : 'bg-violet-50 border-violet-200 text-violet-700',
      cyan: dm ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 'bg-cyan-50 border-cyan-200 text-cyan-700',
      slate: dm ? 'bg-slate-500/10 border-slate-500/20 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-600',
    };
    return (
      <div className={`flex items-start gap-2 mt-3 p-2.5 rounded-lg border text-xs ${colors[d.color]}`}>
        <span className="font-bold shrink-0">{d.icon}</span>
        <span>{d.text}</span>
      </div>
    );
  };

  // ---------- data fetching ----------
  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const [overviewRes, predictionsRes, simulacroRes] = await Promise.all([
        fetch(`${apiUrl}/progress/overview`),
        fetch(`${apiUrl}/progress/predictions`).catch(() => null),
        fetch(`${apiUrl}/progress/last-simulacro`).catch(() => null),
      ]);
      const data = await overviewRes.json();
      setOverviewData(data);
      if (predictionsRes?.ok) setPredictionsData(await predictionsRes.json());
      if (simulacroRes?.ok) setLastSimulacro(await simulacroRes.json());
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAreaDetail = async (areaName) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/progress/area/${encodeURIComponent(areaName)}`);
      const data = await res.json();
      setAreaDetailData(data);
      setSelectedArea(areaName);
      setView('area');
      setShowUnevaluated(false);
    } catch (err) {
      console.error('Error fetching area detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopicDetail = async (area, topic) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiUrl}/progress/topic-detail?area=${encodeURIComponent(area)}&topic=${encodeURIComponent(topic)}`
      );
      const data = await res.json();
      setTopicTrendData(data);
      setSelectedTopic(topic);
      setView('topicTrend');
    } catch (err) {
      console.error('Error fetching topic detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubtopicDetail = async (area, topic, subtopic) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiUrl}/progress/subtopic-detail?area=${encodeURIComponent(area)}&topic=${encodeURIComponent(topic)}&subtopic=${encodeURIComponent(subtopic)}`
      );
      const data = await res.json();
      setSubtopicDetailData(data);
      setSelectedSubtopic(subtopic);
      setView('subtopicDetail');
    } catch (err) {
      console.error('Error fetching subtopic detail:', err);
    } finally {
      setLoading(false);
    }
  };

  // ---------- shared UI pieces ----------
  const cardBase = `${darkMode ? 'bg-slate-800/90 border-slate-700/60' : 'bg-white border-gray-200/70'} border rounded-2xl p-6 shadow-lg`;
  const pageBg = `${darkMode ? 'bg-slate-900' : 'bg-gray-50'}`;
  const textPrimary = darkMode ? 'text-white' : 'text-slate-800';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-slate-500';
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400';

  // ---------- loading state ----------
  if (loading) {
    return (
      <div className={`min-h-screen ${pageBg} flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-3">
          <div className={`w-10 h-10 border-4 ${darkMode ? 'border-slate-700 border-t-emerald-400' : 'border-gray-200 border-t-emerald-500'} rounded-full animate-spin`} />
          <p className={textSecondary}>Cargando...</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  //  VIEW 4 — Subtopic Detail (subtopic_specific breakdown)
  // ===================================================================
  if (view === 'subtopicDetail' && subtopicDetailData) {
    const { specifics, unevaluatedSpecifics, dataPoints: subDataPoints } = subtopicDetailData;
    const subtopicFromTopic = topicTrendData?.subtopics?.find(s => s.subtopic === selectedSubtopic);
    const subComposite = subtopicFromTopic?.composite;
    const subScore = subComposite ? n(subComposite.score) : (subtopicFromTopic ? n(subtopicFromTopic.accuracy) : null);
    const subColor = subScore !== null ? getScoreColor(subScore) : null;

    const chartData4 = (subDataPoints || []).map(d => ({
      ...d,
      date: new Date(d.completedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }),
    }));

    const measurableSpecifics = (specifics || []).filter(s => n(s.total) >= 1);
    const totalSpecCorrect = measurableSpecifics.reduce((sum, s) => sum + n(s.correct), 0);
    const totalSpecTotal = measurableSpecifics.reduce((sum, s) => sum + n(s.total), 0);

    const sortedSpecifics = [...measurableSpecifics].sort((a, b) => {
      const sa = a.composite ? n(a.composite.score) : n(a.accuracy);
      const sb = b.composite ? n(b.composite.score) : n(b.accuracy);
      return sa - sb;
    });

    const premiumItems4 = sortedSpecifics.map(s => ({
      name: s.subtopic_specific || 'Sin detalle',
      fullName: s.subtopic_specific,
      id: s.subtopic_specific,
      score: Math.round(s.composite ? n(s.composite.score) : n(s.accuracy)),
      accuracy: n(s.accuracy),
      correct: n(s.correct),
      total: n(s.total),
      trend: s.trend,
      composite: s.composite,
      bankCount: s.bankCount,
      color: getScoreColor(s.composite ? n(s.composite.score) : n(s.accuracy)),
    }));

    return (
      <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* breadcrumb */}
          <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            onClick={() => { setView('topicTrend'); setSubtopicDetailData(null); setSelectedSubtopic(null); setShowUnevaluated(false); }}
            className={`flex items-center gap-2 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /><span>Volver a {selectedTopic}</span>
          </motion.button>

          {/* ---- HERO HEADER ---- */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`relative overflow-hidden rounded-2xl border p-6 md:p-8 shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800/95 to-slate-900 border-slate-700/80' : 'bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 border-gray-200'}`}>
            {subColor && <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-15 ${subColor.bg === 'bg-emerald-500' ? 'bg-emerald-500' : subColor.bg === 'bg-amber-500' ? 'bg-amber-500' : 'bg-rose-500'}`} />}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-slate-500'}`}>{selectedArea}</span>
                  <span className={`text-xs ${textMuted}`}>{'>'}</span>
                  <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${darkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>{selectedTopic}</span>
                </div>
                <h1 className={`text-2xl md:text-3xl font-bold mt-2 ${textPrimary}`}>{selectedSubtopic}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <Hash className="w-4 h-4" />{totalSpecTotal} preguntas
                  </span>
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <CheckCircle className="w-4 h-4" />{totalSpecCorrect} correctas
                  </span>
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <Layers className="w-4 h-4" />{measurableSpecifics.length} detalles evaluados
                  </span>
                </div>
              </div>
              {subScore !== null && (
                <div className="flex items-center gap-5 shrink-0">
                  <div className="relative">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke={darkMode ? '#334155' : '#e2e8f0'} strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none"
                        stroke={subScore >= 80 ? '#10b981' : subScore >= 50 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(subScore / 100) * 264} 264`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-xl font-bold ${subColor.text}`}>{subScore.toFixed(1)}</span>
                      <span className={`text-[10px] ${textMuted}`}>score</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${subColor.light} ${subColor.text}`}>{subColor.label}</span>
                    {subtopicFromTopic?.trend && <div className="flex items-center gap-1">{getTrendIcon(subtopicFromTopic.trend)}<span className={`text-xs ${textMuted}`}>{subtopicFromTopic.trend === 'up' ? 'Mejorando' : subtopicFromTopic.trend === 'down' ? 'Bajando' : 'Estable'}</span></div>}
                  </div>
                </div>
              )}
            </div>
            {subComposite && (
              <div className="mt-4 max-w-md">
                <CompositeIndicators composite={subComposite} total={subtopicFromTopic?.total} bankCount={subtopicFromTopic?.bankCount} dm={darkMode} />
              </div>
            )}
          </motion.div>

          {/* ---- TREND CHART ---- */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className={cardBase}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${textPrimary}`}>Tendencia</h2>
              {chartData4.length > 1 && (
                <span className={`text-xs px-2.5 py-1 rounded-full ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-slate-500'}`}>
                  {chartData4.length} sesiones
                </span>
              )}
            </div>
            {chartData4.length === 0 ? (
              <div className="h-[220px] flex flex-col items-center justify-center gap-3">
                <AlertTriangle className={`w-10 h-10 ${textMuted}`} />
                <p className={textSecondary}>Sin datos de tendencia</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData4} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e2e8f0'} />
                  <XAxis dataKey="date" stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ backgroundColor: darkMode ? '#1e293b' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius: '8px', color: darkMode ? '#f1f5f9' : '#1e293b' }}
                    formatter={(value) => [`${n(value).toFixed(1)}%`, 'Aciertos']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.examTitle || label} />
                  <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="5 5" label={{ value: 'Meta 80%', fill: '#22c55e', fontSize: 11 }} />
                  <Line type="monotone" dataKey="accuracy" stroke="#8b5cf6" strokeWidth={3}
                    dot={{ r: 5, fill: '#8b5cf6', strokeWidth: 2, stroke: darkMode ? '#1e293b' : '#ffffff' }}
                    activeDot={{ r: 8, fill: '#8b5cf6' }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* ---- PREMIUM SUBTOPIC_SPECIFIC BREAKDOWN ---- */}
          <PremiumBreakdown
            items={premiumItems4}
            title={`Desglose de ${selectedSubtopic}`}
            subtitle={`${premiumItems4.length} detalles evaluados · Ordenados por prioridad`}
          />

          {/* unevaluated specifics */}
          {unevaluatedSpecifics && unevaluatedSpecifics.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <button onClick={() => setShowUnevaluated(!showUnevaluated)}
                className={`flex items-center gap-2 text-sm font-medium ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
                {showUnevaluated ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>Detalles sin evaluar ({unevaluatedSpecifics.length})</span>
                {showUnevaluated ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showUnevaluated && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 flex flex-wrap gap-2">
                  {unevaluatedSpecifics.map((sp) => (
                    <span key={sp} className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-slate-600'}`}>{sp}</span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Diagnostic badge for subtopic */}
          {subComposite && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className={cardBase}>
              <h2 className={`text-lg font-semibold mb-2 ${textPrimary}`}>Diagnostico</h2>
              <DiagnosticBadge accuracy={subtopicFromTopic?.accuracy} composite={subComposite} trend={subtopicFromTopic?.trend} dm={darkMode} />
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // ===================================================================
  //  VIEW 3 — Topic Detail (Professional Redesign: subtopics + trend)
  // ===================================================================
  if (view === 'topicTrend' && topicTrendData) {
    const { dataPoints, subtopics: topicSubtopics, unevaluatedSubtopics } = topicTrendData;
    const latestAccuracy = dataPoints && dataPoints.length > 0 ? n(dataPoints[dataPoints.length - 1].accuracy) : null;
    const topicFromArea = areaDetailData?.topics?.find((t) => t.topic === selectedTopic);
    const topicComposite = topicFromArea?.composite;
    const topicScore = topicComposite ? n(topicComposite.score) : latestAccuracy;
    const topicColor = topicScore !== null ? getScoreColor(topicScore) : null;
    const chartData = (dataPoints || []).map((d) => ({
      ...d,
      date: new Date(d.completedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }),
    }));
    const allEvaluatedSubtopics = (topicSubtopics || []).filter(s => n(s.total) > 0);
    const totalSubCorrect = allEvaluatedSubtopics.reduce((sum, s) => sum + n(s.correct), 0);
    const totalSubTotal = allEvaluatedSubtopics.reduce((sum, s) => sum + n(s.total), 0);

    // Sort all evaluated subtopics by score worst first
    const sortedAllEvaluated = [...allEvaluatedSubtopics].sort((a, b) => {
      const sa = a.composite ? n(a.composite.score) : n(a.accuracy);
      const sb = b.composite ? n(b.composite.score) : n(b.accuracy);
      return sa - sb;
    });

    const premiumItems3 = sortedAllEvaluated.map(s => ({
      name: s.subtopic,
      fullName: s.subtopic,
      id: s.subtopic,
      score: Math.round(s.composite ? n(s.composite.score) : n(s.accuracy)),
      accuracy: n(s.accuracy),
      correct: n(s.correct),
      total: n(s.total),
      trend: s.trend,
      composite: s.composite,
      bankCount: s.bankCount,
      color: getScoreColor(s.composite ? n(s.composite.score) : n(s.accuracy)),
    }));

    return (
      <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* breadcrumb */}
          <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            onClick={() => { setView('area'); setTopicTrendData(null); setSelectedTopic(null); setShowUnevaluated(false); }}
            className={`flex items-center gap-2 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /><span>Volver a {selectedArea}</span>
          </motion.button>

          {/* ---- HERO HEADER ---- */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`relative overflow-hidden rounded-2xl border p-6 md:p-8 shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800/95 to-slate-900 border-slate-700/80' : 'bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 border-gray-200'}`}>
            {topicColor && <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-15 ${topicColor.bg === 'bg-emerald-500' ? 'bg-emerald-500' : topicColor.bg === 'bg-amber-500' ? 'bg-amber-500' : 'bg-rose-500'}`} />}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-slate-500'}`}>{selectedArea}</span>
                </div>
                <h1 className={`text-2xl md:text-3xl font-bold mt-2 ${textPrimary}`}>{selectedTopic}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <Hash className="w-4 h-4" />{totalSubTotal} preguntas
                  </span>
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <CheckCircle className="w-4 h-4" />{totalSubCorrect} correctas
                  </span>
                  <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                    <Layers className="w-4 h-4" />{allEvaluatedSubtopics.length} subtemas evaluados
                  </span>
                </div>
              </div>
              {topicScore !== null && (
                <div className="flex items-center gap-5 shrink-0">
                  <div className="relative">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke={darkMode ? '#334155' : '#e2e8f0'} strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none"
                        stroke={topicScore >= 80 ? '#10b981' : topicScore >= 50 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(topicScore / 100) * 264} 264`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-xl font-bold ${topicColor.text}`}>{n(topicScore).toFixed(1)}</span>
                      <span className={`text-[10px] ${textMuted}`}>score</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${topicColor.light} ${topicColor.text}`}>{topicColor.label}</span>
                    {latestAccuracy !== null && <p className={`text-sm ${textMuted}`}>Aciertos: {n(latestAccuracy).toFixed(1)}%</p>}
                    {topicFromArea?.trend && <div className="flex items-center gap-1">{getTrendIcon(topicFromArea.trend)}<span className={`text-xs ${textMuted}`}>{topicFromArea.trend === 'up' ? 'Mejorando' : topicFromArea.trend === 'down' ? 'Bajando' : 'Estable'}</span></div>}
                  </div>
                </div>
              )}
            </div>
            {topicComposite && (
              <div className="mt-4 max-w-md">
                <CompositeIndicators composite={topicComposite} total={topicFromArea?.total} bankCount={topicFromArea?.bankCount} dm={darkMode} />
              </div>
            )}
          </motion.div>

          {/* ---- TREND CHART + MINI STATS ROW ---- */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Trend chart - takes 2 cols */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className={`lg:col-span-2 ${cardBase}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-semibold ${textPrimary}`}>Tendencia de Aciertos</h2>
                {dataPoints && dataPoints.length > 1 && (
                  <span className={`text-xs px-2.5 py-1 rounded-full ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-slate-500'}`}>
                    {dataPoints.length} sesiones
                  </span>
                )}
              </div>
              {(!dataPoints || dataPoints.length === 0) ? (
                <div className="h-[280px] flex flex-col items-center justify-center gap-3">
                  <AlertTriangle className={`w-10 h-10 ${textMuted}`} />
                  <p className={textSecondary}>Sin datos de tendencia aun</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e2e8f0'} />
                    <XAxis dataKey="date" stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={{ backgroundColor: darkMode ? '#1e293b' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius: '8px', color: darkMode ? '#f1f5f9' : '#1e293b' }}
                      formatter={(value) => [`${n(value).toFixed(1)}%`, 'Aciertos']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.examTitle || label} />
                    <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="5 5" label={{ value: 'Meta 80%', fill: '#22c55e', fontSize: 11 }} />
                    <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Alerta', fill: '#f59e0b', fontSize: 11 }} />
                    <Line type="monotone" dataKey="accuracy" stroke="#6366f1" strokeWidth={3}
                      dot={{ r: 5, fill: '#6366f1', strokeWidth: 2, stroke: darkMode ? '#1e293b' : '#ffffff' }}
                      activeDot={{ r: 8, fill: '#6366f1' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </motion.div>

            {/* Session detail - compact card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className={cardBase}>
              <h2 className={`text-lg font-semibold mb-3 ${textPrimary}`}>Historial</h2>
              {dataPoints && dataPoints.length > 0 ? (
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {dataPoints.slice().reverse().map((dp, i) => {
                    const dpAcc = n(dp.accuracy);
                    const dpColor = getScoreColor(dpAcc);
                    return (
                      <div key={dp.sessionId || i}
                        className={`p-3 rounded-xl border transition-colors ${darkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${textPrimary}`} title={dp.examTitle}>{dp.examTitle || 'Examen'}</p>
                            <p className={`text-[11px] ${textMuted}`}>
                              {new Date(dp.completedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })}
                              {' · '}{dp.correct}/{dp.total}
                            </p>
                          </div>
                          <span className={`text-lg font-bold ${dpColor.text}`}>{dpAcc.toFixed(0)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[280px] flex items-center justify-center">
                  <p className={`text-sm ${textMuted}`}>Sin sesiones</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* ---- PREMIUM SUBTOPIC BREAKDOWN (clickable for drill-down) ---- */}
          <PremiumBreakdown
            items={premiumItems3}
            title={`Subtemas de ${selectedTopic}`}
            subtitle={`${premiumItems3.length} subtemas evaluados · Click para profundizar`}
            onItemClick={(item) => fetchSubtopicDetail(selectedArea, selectedTopic, item.id)}
          />

          {/* unevaluated subtopics */}
          {unevaluatedSubtopics && unevaluatedSubtopics.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <button onClick={() => setShowUnevaluated(!showUnevaluated)}
                className={`flex items-center gap-2 text-sm font-medium ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
                {showUnevaluated ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>Subtemas sin evaluar ({unevaluatedSubtopics.length})</span>
                {showUnevaluated ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showUnevaluated && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 flex flex-wrap gap-2">
                  {unevaluatedSubtopics.map((st) => (
                    <span key={st} className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-slate-600'}`}>{st}</span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // ===================================================================
  //  VIEW 2 — Area Detail (Professional Redesign)
  // ===================================================================
  if (view === 'area' && areaDetailData) {
    const { area, topics, unevaluatedTopics } = areaDetailData;
    const areaOverview = overviewData?.areas?.find((a) => a.area === area);
    const areaComposite = areaOverview?.composite;
    const areaScore = areaComposite ? n(areaComposite.score) : (areaOverview ? n(areaOverview.accuracy) : null);
    const areaAcc = areaOverview ? n(areaOverview.accuracy) : null;
    const areaColor = areaScore !== null ? getScoreColor(areaScore) : null;

    // Prediction data for this area
    const areaPrediction = predictionsData?.topics?.filter(t => t.area === area) || [];

    // Stats for this area
    const totalTopics = topics.length;
    const greenTopics = topics.filter(t => (t.composite ? n(t.composite.score) : n(t.accuracy)) >= 80).length;
    const amberTopics = topics.filter(t => { const s = t.composite ? n(t.composite.score) : n(t.accuracy); return s >= 50 && s < 80; }).length;
    const redTopics = topics.filter(t => (t.composite ? n(t.composite.score) : n(t.accuracy)) < 50).length;
    const totalAnswered = topics.reduce((sum, t) => sum + n(t.total), 0);
    const totalBank = topics.reduce((sum, t) => sum + n(t.bankCount || 0), 0);

    // Sort topics by score (worst first for priority)
    const sortedTopics = [...topics].sort((a, b) => {
      const scoreA = a.composite ? n(a.composite.score) : n(a.accuracy);
      const scoreB = b.composite ? n(b.composite.score) : n(b.accuracy);
      return scoreA - scoreB;
    });

    return (
      <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* breadcrumb */}
          <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            onClick={() => { setView('overview'); setAreaDetailData(null); setSelectedArea(null); setShowUnevaluated(false); }}
            className={`flex items-center gap-2 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /><span>Volver al resumen</span>
          </motion.button>

          {/* ---- HERO HEADER ---- */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`relative overflow-hidden rounded-2xl border p-6 md:p-8 shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800/95 to-slate-900 border-slate-700/80' : 'bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 border-gray-200'}`}>
            {areaColor && <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-15 bg-gradient-to-br ${areaColor.bg === 'bg-emerald-500' ? 'from-emerald-500 to-green-400' : areaColor.bg === 'bg-amber-500' ? 'from-amber-500 to-yellow-400' : 'from-rose-500 to-orange-400'}`} />}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Area</p>
                <h1 className={`text-2xl md:text-3xl font-bold mt-1 ${textPrimary}`}>{area}</h1>
                {areaOverview && (
                  <div className="flex flex-wrap items-center gap-4 mt-3">
                    <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                      <Hash className="w-4 h-4" />{totalAnswered}/{totalBank || '?'} preguntas
                    </span>
                    <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                      <BookOpen className="w-4 h-4" />{areaOverview.sessions} sesiones
                    </span>
                    <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                      <Layers className="w-4 h-4" />{totalTopics} temas
                    </span>
                  </div>
                )}
              </div>
              {areaScore !== null && (
                <div className="flex items-center gap-5 shrink-0">
                  {/* Big score circle */}
                  <div className="relative">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke={darkMode ? '#334155' : '#e2e8f0'} strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none"
                        stroke={areaScore >= 80 ? '#10b981' : areaScore >= 50 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(areaScore / 100) * 264} 264`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-xl font-bold ${areaColor.text}`}>{areaScore.toFixed(1)}</span>
                      <span className={`text-[10px] ${textMuted}`}>score</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${areaColor.light} ${areaColor.text}`}>
                      {areaColor.label}
                    </span>
                    {areaAcc !== null && <p className={`text-sm ${textMuted}`}>Aciertos: {areaAcc.toFixed(1)}%</p>}
                    {areaOverview?.trend && <div className="flex items-center gap-1">{getTrendIcon(areaOverview.trend)}<span className={`text-xs ${textMuted}`}>{areaOverview.trend === 'up' ? 'Mejorando' : areaOverview.trend === 'down' ? 'Bajando' : 'Estable'}</span></div>}
                  </div>
                </div>
              )}
            </div>
            {/* Composite indicators */}
            {areaComposite && (
              <div className="mt-4 max-w-md">
                <CompositeIndicators composite={areaComposite} total={areaOverview?.total} bankCount={areaOverview?.bankCount} dm={darkMode} />
              </div>
            )}
          </motion.div>

          {/* ---- MINI STAT CARDS (premium) ---- */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Dominados" value={greenTopics} icon={CheckCircle} accentColor="green" />
            <StatCard title="En Progreso" value={amberTopics} icon={AlertTriangle} accentColor="amber" />
            <StatCard title="Reforzar" value={redTopics} icon={Flame} accentColor="rose" />
            <StatCard title="Sin Evaluar" value={unevaluatedTopics?.length || 0} icon={Eye} accentColor="purple" />
          </div>

          {/* ---- BAR CHART FOR TOPICS ---- */}
          {(() => {
            const barTopics = sortedTopics.map(t => ({
              name: t.topic.length > 25 ? t.topic.substring(0, 24) + '...' : t.topic,
              fullName: t.topic,
              score: Math.round(t.composite ? n(t.composite.score) : n(t.accuracy)),
            }));
            if (barTopics.length < 2) return null;
            return (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
                className={cardBase}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-semibold ${textPrimary}`}>Distribucion por Tema</h2>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${darkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>Meta: 80%</span>
                </div>
                <ScoreBarChart data={barTopics} height={420} />
              </motion.div>
            );
          })()}

          {/* ---- TOPIC LIST (sorted worst first) ---- */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-semibold ${textPrimary}`}>Temas ({totalTopics})</h2>
              <span className={`text-xs ${textMuted}`}>Ordenados por prioridad</span>
            </div>
            {sortedTopics.map((topic, index) => {
              const topicScore = topic.composite ? n(topic.composite.score) : n(topic.accuracy);
              const topicAcc = n(topic.accuracy);
              const color = getScoreColor(topicScore);
              const pred = areaPrediction.find(p => p.topic === topic.topic);
              const isTop3 = index < 3 && topicScore < 80;
              return (
                <motion.div
                  key={topic.topic}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.04 }}
                  onClick={() => fetchTopicDetail(area, topic.topic)}
                  className={`rounded-2xl border p-5 cursor-pointer transition-all duration-200 hover:scale-[1.01] hover:shadow-lg ${
                    isTop3
                      ? darkMode ? 'bg-gradient-to-r from-rose-500/8 to-transparent border-rose-500/25 shadow-md' : 'bg-gradient-to-r from-rose-50 to-transparent border-rose-200 shadow-md'
                      : darkMode ? 'bg-slate-800 border-slate-700 shadow-sm' : 'bg-white border-gray-200 shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank + Score circle */}
                    <div className="relative shrink-0">
                      <svg className="w-14 h-14 -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="40" fill="none" stroke={darkMode ? '#334155' : '#e2e8f0'} strokeWidth="7" />
                        <circle cx="50" cy="50" r="40" fill="none"
                          stroke={topicScore >= 80 ? '#10b981' : topicScore >= 50 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="7" strokeLinecap="round"
                          strokeDasharray={`${(topicScore / 100) * 251} 251`} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-sm font-bold ${color.text}`}>{topicScore.toFixed(0)}</span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold ${textPrimary}`}>{topic.topic}</h3>
                        {isTop3 && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700'}`}>
                            <Flame className="h-3 w-3" /> Prioridad
                          </span>
                        )}
                        {getTrendIcon(topic.trend)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        <span className={`text-xs ${textMuted}`}>{topic.total}/{topic.bankCount || '?'} preguntas</span>
                        <span className={`text-xs ${textMuted}`}>{topic.sessions} sesiones</span>
                        <span className={`text-xs ${textMuted}`}>Aciertos: {topicAcc.toFixed(1)}%</span>
                        {pred && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>~{pred.expected} esperadas RM</span>}
                      </div>
                      {/* Progress bar */}
                      <div className={`mt-2.5 h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div className={`h-full rounded-full transition-all duration-500 ${color.bg}`} style={{ width: `${Math.min(topicScore, 100)}%` }} />
                      </div>
                      {/* Composite */}
                      {topic.composite && <CompositeIndicators composite={topic.composite} total={topic.total} bankCount={topic.bankCount} dm={darkMode} />}
                    </div>

                    {/* Badge + Arrow */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${color.light} ${color.text}`}>{color.label}</span>
                      <ArrowRight className={`w-4 h-4 ${textMuted}`} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* unevaluated topics */}
          {unevaluatedTopics && unevaluatedTopics.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <button onClick={() => setShowUnevaluated(!showUnevaluated)}
                className={`flex items-center gap-2 text-sm font-medium ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
                {showUnevaluated ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>Temas sin evaluar ({unevaluatedTopics.length})</span>
                {showUnevaluated ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showUnevaluated && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 flex flex-wrap gap-2">
                  {unevaluatedTopics.map((t) => (
                    <span key={t} className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-slate-600'}`}>{t}</span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // ===================================================================
  //  VIEW 4 — Guide (Como funciona)
  // ===================================================================
  if (view === 'guide') {
    const guideCard = `${cardBase} space-y-4`;
    const stepNum = (num, color = 'from-emerald-500 to-teal-500') => (
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg`}>
        {num}
      </div>
    );
    const tipCard = (text) => (
      <div className={`flex items-start gap-3 p-4 rounded-xl ${darkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
        <Lightbulb className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <p className={`text-sm ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>{text}</p>
      </div>
    );

    return (
      <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
        <div className="max-w-4xl mx-auto space-y-6">
          {/* breadcrumb */}
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => setView('overview')}
            className={`flex items-center gap-2 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al dashboard</span>
          </motion.button>

          {/* header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-2">
              <HelpCircle className={`w-8 h-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary}`}>Como funciona Mi Progreso</h1>
            </div>
            <p className={textSecondary}>Aprende a interpretar tu dashboard y mejorar tu rendimiento</p>
          </motion.div>

          {/* Section 1: Que es */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <BarChart3 className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Que es este dashboard
            </h2>
            <p className={textSecondary}>
              Cada vez que das un examen o simulacro, el sistema guarda <strong className={textPrimary}>cada una de tus respuestas</strong> clasificada por area, tema y subtema. Este dashboard toma todas esas respuestas y calcula un <strong className={textPrimary}>Score Compuesto</strong> que refleja tu nivel real en cada tema — no solo cuantas acertaste, sino que tan confiable es esa medicion.
            </p>
            <p className={textSecondary}>
              El sistema mide en <strong className={textPrimary}>3 niveles</strong>: Area {'>'} Topic {'>'} Subtopic. Por ejemplo: Infectologia {'>'} ITS {'>'} Sifilis. Asi puedes saber exactamente donde reforzar.
            </p>
            {tipCard('Mientras mas examenes des, mas preciso sera tu score. Un solo examen te da una foto borrosa; varios te dan la pelicula completa.')}
          </motion.div>

          {/* Section 2: Score Compuesto */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <Target className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Como se calcula tu Score
            </h2>
            <p className={`text-sm ${textSecondary}`}>
              El numero grande que ves en cada tarjeta es tu <strong className={textPrimary}>Score Compuesto</strong>. Se basa en tus aciertos pero lo ajusta con 4 factores:
            </p>

            {/* Factor 1: Confianza */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <h3 className={`font-semibold ${textPrimary}`}>Confianza (barra azul)</h3>
              </div>
              <p className={`text-sm ${textSecondary}`}>
                <strong className={textPrimary}>Cuantas preguntas respondiste?</strong> Con pocas preguntas, el score se acerca a 30% (incertidumbre). Con 10+ preguntas, tus aciertos se respetan tal cual.
              </p>
              <p className={`text-xs mt-2 ${textMuted}`}>
                Ejemplo: 2 de 2 correctas = aciertos 100%, pero score ~60% (poca confianza). Con 10 de 10 correctas, score si es ~100%.
              </p>
            </div>

            {/* Factor 2: Cobertura */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-violet-500" />
                <h3 className={`font-semibold ${textPrimary}`}>Cobertura (barra violeta)</h3>
              </div>
              <p className={`text-sm ${textSecondary}`}>
                <strong className={textPrimary}>Que % del banco de preguntas has visto?</strong> Si un tema tiene 50 preguntas y solo respondiste 5, tu cobertura es 10%. Puede que no hayas visto las preguntas dificiles.
              </p>
              <p className={`text-xs mt-2 ${textMuted}`}>
                Se muestra como "5/50 preguntas" en la tarjeta. No modifica el score directamente, pero te indica si tu medicion es representativa.
              </p>
            </div>

            {/* Factor 3: Vigencia */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                <h3 className={`font-semibold ${textPrimary}`}>Vigencia (barra cyan)</h3>
              </div>
              <p className={`text-sm ${textSecondary}`}>
                <strong className={textPrimary}>Hace cuanto respondiste?</strong> El conocimiento se olvida. Si sacaste 90% hace 3 meses y no repasaste, tu nivel real ya no es 90%.
              </p>
              <p className={`text-xs mt-2 ${textMuted}`}>
                Ayer = ~100% vigencia. Hace 1 mes = ~70%. Hace 3 meses = ~35%. El score baja gradualmente si no repasas.
              </p>
            </div>

            {/* Factor 4: Tendencia */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <h3 className={`font-semibold ${textPrimary}`}>Tendencia (flecha)</h3>
              </div>
              <p className={`text-sm ${textSecondary}`}>
                <strong className={textPrimary}>Estas mejorando o empeorando?</strong> Compara tus ultimas 3 sesiones vs las 3 anteriores. Si mejoras, +3 puntos al score. Si empeoras, -3 puntos.
              </p>
            </div>

            {tipCard('Un score de 80+ con las 3 barras altas significa que realmente dominas el tema. Un score alto con barras bajas significa que necesitas mas evidencia.')}
          </motion.div>

          {/* Section 3: Las 3 vistas */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <Layers className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Las 3 vistas (de lo general a lo especifico)
            </h2>
            <div className="space-y-4">
              {/* Vista 1 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                {stepNum(1)}
                <div className="flex-1">
                  <h3 className={`font-semibold ${textPrimary}`}>Resumen General (Areas)</h3>
                  <p className={`text-sm mt-1 ${textSecondary}`}>
                    Vista principal. Muestra tus estadisticas globales y un <strong className={textPrimary}>grid con todas las areas medicas</strong>, cada una con su score, aciertos, barras de indicadores y tendencia.
                  </p>
                </div>
              </div>
              {/* Vista 2 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                {stepNum(2, 'from-blue-500 to-indigo-500')}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${textPrimary}`}>Detalle por Area (Topics)</h3>
                    <MousePointerClick className={`w-4 h-4 ${textMuted}`} />
                  </div>
                  <p className={`text-sm mt-1 ${textSecondary}`}>
                    Click en un area {'>'} ves todos los <strong className={textPrimary}>temas dentro</strong> con score, barras y tendencia. Tambien los temas que aun no has evaluado.
                  </p>
                </div>
              </div>
              {/* Vista 3 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                {stepNum(3, 'from-violet-500 to-purple-500')}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${textPrimary}`}>Detalle por Topic (Subtopics + Tendencia)</h3>
                    <MousePointerClick className={`w-4 h-4 ${textMuted}`} />
                  </div>
                  <p className={`text-sm mt-1 ${textSecondary}`}>
                    Click en un tema {'>'} ves el <strong className={textPrimary}>desglose por subtema</strong> (ej: dentro de ITS ves Sifilis, VIH, etc.) mas un <strong className={textPrimary}>grafico de tendencia</strong> con tus aciertos en cada examen a lo largo del tiempo.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Colores */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <Target className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Colores y etiquetas
            </h2>
            <p className={`text-sm ${textSecondary}`}>Los colores se basan en el <strong className={textPrimary}>score compuesto</strong> (no en los aciertos crudos):</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className={`p-4 rounded-xl border ${darkMode ? 'bg-rose-500/10 border-rose-500/30' : 'bg-rose-50 border-rose-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full bg-rose-500" />
                  <span className={`font-bold text-rose-500`}>Reforzar</span>
                </div>
                <p className={`text-sm ${darkMode ? 'text-rose-300' : 'text-rose-700'}`}>
                  Score menor a 50%. Puede ser por aciertos bajos, pocas preguntas, o tema viejo sin repasar.
                </p>
              </div>
              <div className={`p-4 rounded-xl border ${darkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500" />
                  <span className={`font-bold text-amber-500`}>En progreso</span>
                </div>
                <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Score entre 50% y 79%. Vas bien. Sube respondiendo mas preguntas y repasando reciente.
                </p>
              </div>
              <div className={`p-4 rounded-xl border ${darkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full bg-emerald-500" />
                  <span className={`font-bold text-emerald-500`}>Dominado</span>
                </div>
                <p className={`text-sm ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  Score 80%+. Nivel confirmado con buena evidencia. Solo necesitas repaso periodico.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Como interpretar */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <Lightbulb className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Como interpretar las tarjetas
            </h2>
            <p className={`text-sm ${textSecondary}`}>Cada tarjeta muestra un score grande + aciertos + 3 barras. Lee asi:</p>
            <div className="space-y-3">
              {[
                { emoji: '', label: 'Score alto + 3 barras altas', desc: 'Dominas el tema con certeza. Mantenerlo con repaso periodico.' },
                { emoji: '', label: 'Score alto + Confianza baja', desc: 'Parece que vas bien pero pocas preguntas. Responde mas para confirmarlo.' },
                { emoji: '', label: 'Score alto + Cobertura baja', desc: 'Cuidado: no has visto la mayoria del banco. Podrias tener sorpresas.' },
                { emoji: '', label: 'Score alto + Vigencia baja', desc: 'Hace mucho que no tocas este tema. Necesitas repasar pronto.' },
                { emoji: '', label: 'Score bajo + Tendencia subiendo', desc: 'Vas mejorando. Sigue estudiando y el score subira.' },
              ].map((item, i) => (
                <div key={i} className={`p-3 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                  <p className={`font-semibold text-sm ${textPrimary}`}>{item.label}</p>
                  <p className={`text-sm mt-1 ${textSecondary}`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Section 6: Ciclo de estudio */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <RefreshCcw className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Ciclo de mejora
            </h2>
            <div className="space-y-0">
              {[
                { num: 1, color: 'from-blue-500 to-cyan-500', title: 'Da un simulacro', desc: 'Examen completo para medir tu nivel base en todas las areas.' },
                { num: 2, color: 'from-orange-500 to-red-500', title: 'Identifica debilidades', desc: 'Mira los scores rojos. Revisa las barras: confianza baja? Vigencia baja? Cobertura baja?' },
                { num: 3, color: 'from-violet-500 to-purple-500', title: 'Profundiza', desc: 'Click en area > topic > subtopic. Encuentra exactamente que tema reforzar.' },
                { num: 4, color: 'from-emerald-500 to-green-500', title: 'Estudia y re-evalua', desc: 'Estudia el tema, da otro examen. El dashboard se actualiza automaticamente.' },
                { num: 5, color: 'from-amber-500 to-yellow-500', title: 'Verifica', desc: 'El score subio? El color cambio? La confianza y cobertura crecieron?' },
              ].map((step, i) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    {stepNum(step.num, step.color)}
                    {i < 4 && <div className={`w-0.5 h-8 ${darkMode ? 'bg-slate-700' : 'bg-gray-300'}`} />}
                  </div>
                  <div className="pt-2 pb-4">
                    <h3 className={`font-semibold ${textPrimary}`}>{step.title}</h3>
                    <p className={`text-sm mt-1 ${textSecondary}`}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className={`text-center p-4 rounded-xl ${darkMode ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'}`}>
              <RefreshCcw className={`w-6 h-6 mx-auto mb-2 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              <p className={`text-sm font-medium ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                Repite hasta que todos los scores esten en verde (80%+) con barras altas
              </p>
            </div>
          </motion.div>

          {/* Section 7: Preguntas frecuentes */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className={guideCard}>
            <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
              <HelpCircle className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
              Preguntas frecuentes
            </h2>
            <div className="space-y-3">
              {[
                { q: 'Por que mi score es mas bajo que mis aciertos?', a: 'Puede ser por: pocas preguntas (confianza baja), hace mucho que no repasas (vigencia baja), o tendencia negativa. Mira las barras para identificar que factor te baja.' },
                { q: 'Como subo mi score rapido?', a: 'Responde mas preguntas del banco (sube confianza y cobertura), repasa temas viejos (mantiene vigencia), y mejora tus aciertos estudiando antes de re-evaluar.' },
                { q: 'Mi score bajo sin dar examenes nuevos?', a: 'Normal. La vigencia decae con el tiempo. Si no tocas un tema por semanas, el score baja reflejando que ese conocimiento puede haberse oxidado. Solucion: repasar periodicamente.' },
                { q: 'Puedo ver mi nivel en Sifilis especificamente?', a: 'Si. Area (Infectologia) > Topic (ITS) > ahi veras Sifilis como subtopic con su propio score, aciertos y barras.' },
                { q: 'Se actualiza automaticamente?', a: 'Si. Cada vez que abres Mi Progreso, calcula todo fresco desde la base de datos.' },
                { q: 'Y si doy examenes mixtos?', a: 'Perfecto. El sistema desglosa cada respuesta por su area, topic y subtopic individual.' },
              ].map((faq, i) => (
                <div key={i} className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                  <p className={`font-semibold text-sm ${textPrimary}`}>{faq.q}</p>
                  <p className={`text-sm mt-1 ${textSecondary}`}>{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA to go back */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-center pb-8">
            <button
              onClick={() => setView('overview')}
              className={`px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 ${darkMode ? 'bg-emerald-500 text-white hover:bg-emerald-400' : 'bg-emerald-500 text-white hover:bg-emerald-600'} shadow-lg`}
            >
              Ir al Dashboard
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ===================================================================
  //  VIEW 1 — Overview (default)
  // ===================================================================
  if (!overviewData) {
    return (
      <div className={`min-h-screen ${pageBg} flex items-center justify-center`}>
        <p className={textSecondary}>Error al cargar los datos. Intenta de nuevo.</p>
      </div>
    );
  }

  const { summary, areas, unevaluatedAreas } = overviewData;

  // empty state
  if (summary.totalSessions === 0) {
    return (
      <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
        <div className="max-w-5xl mx-auto">
          <button onClick={onBack} className={`flex items-center gap-2 mb-8 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">Volver</span>
          </button>
          <div className={`${cardBase} text-center py-20`}>
            <BookOpen className={`w-16 h-16 mx-auto mb-6 ${textMuted}`} />
            <h2 className={`text-xl font-semibold mb-2 ${textPrimary}`}>Sin datos aun</h2>
            <p className={`max-w-md mx-auto ${textSecondary}`}>Da tu primer simulacro para comenzar a medir tu progreso.</p>
            <button onClick={() => setView('guide')} className={`mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-105 ${darkMode ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/25' : 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100'}`}>
              <HelpCircle className="w-4 h-4" /><span>Como funciona este dashboard</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Computed data for professional overview ----
  const readinessScore = (() => {
    if (!areas.length || !predictionsData?.areas?.length) return n(summary.overallAccuracy);
    let weightedSum = 0, weightTotal = 0;
    areas.forEach(a => {
      const pred = predictionsData.areas.find(p => p.area === a.area);
      const weight = pred ? parseFloat(pred.expected) : 1;
      const score = a.composite ? n(a.composite.score) : n(a.accuracy);
      weightedSum += score * weight;
      weightTotal += weight;
    });
    return weightTotal > 0 ? weightedSum / weightTotal : n(summary.overallAccuracy);
  })();

  const priorityAreas = (() => {
    if (!predictionsData?.areas?.length) return areas.map(a => ({ ...a, expected: 0, impact: 0 }));
    return areas.map(a => {
      const pred = predictionsData.areas.find(p => p.area === a.area);
      const expected = pred ? parseFloat(pred.expected) : 0;
      const score = a.composite ? n(a.composite.score) : n(a.accuracy);
      const impact = expected * (1 - score / 100);
      return { ...a, expected, impact, score };
    }).sort((a, b) => b.impact - a.impact);
  })();

  const barDataAreas = (() => {
    return [...areas]
      .map(a => ({
        name: a.area.length > 25 ? a.area.substring(0, 24) + '...' : a.area,
        fullName: a.area,
        score: Math.round(a.composite ? n(a.composite.score) : n(a.accuracy)),
      }))
      .sort((a, b) => a.score - b.score);
  })();

  const getReadinessColor = (score) => {
    if (score >= 80) return { gradient: 'from-emerald-500 to-green-400', text: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'En camino' };
    if (score >= 60) return { gradient: 'from-amber-500 to-yellow-400', text: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', label: 'Progresando' };
    return { gradient: 'from-rose-500 to-orange-400', text: 'text-rose-400', bg: 'bg-rose-500/15', border: 'border-rose-500/30', label: 'Reforzar' };
  };
  const readColor = getReadinessColor(readinessScore);

  const bestArea = [...areas].sort((a, b) => {
    const sa = a.composite ? n(a.composite.score) : n(a.accuracy);
    const sb = b.composite ? n(b.composite.score) : n(b.accuracy);
    return sb - sa;
  })[0] || null;

  const simInfo = lastSimulacro?.hasData ? lastSimulacro : null;

  return (
    <div className={`min-h-screen ${pageBg} p-4 md:p-8`}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* back button */}
        <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} onClick={onBack}
          className={`flex items-center gap-2 ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Volver</span>
        </motion.button>

        {/* title */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className={`text-3xl md:text-4xl font-bold ${textPrimary}`}>Mi Progreso</h1>
          <p className={`mt-1 ${textSecondary}`}>Dashboard de rendimiento y predicciones RM 2026</p>
        </motion.div>

        {/* ======== 5 STAT CARDS (premium, matching YearAnalysis) ======== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Score Global"
            value={`${readinessScore.toFixed(1)}%`}
            subtitle={`Meta: 80% · Faltan ${Math.max(0, (80 - readinessScore)).toFixed(1)} pts`}
            icon={Shield}
            accentColor={readinessScore >= 80 ? 'green' : readinessScore >= 60 ? 'amber' : 'rose'}
          >
            <div className={`mt-3 h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
              <div className={`h-full rounded-full bg-gradient-to-r ${readColor.gradient} transition-all duration-700`} style={{ width: `${Math.min(readinessScore, 100)}%` }} />
            </div>
          </StatCard>

          <StatCard title="Preguntas" value={summary.totalAnswers}
            subtitle={`${summary.totalSessions} examenes · ${summary.studyDays} dias`}
            icon={Hash} accentColor="blue" />

          <StatCard title="Areas Evaluadas" value={areas.length}
            subtitle={`${areas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) >= 80).length} dominadas`}
            icon={Layers} accentColor="purple" />

          <StatCard title="Area mas Fuerte"
            value={bestArea ? bestArea.area : 'N/A'}
            subtitle={bestArea ? `${(bestArea.composite ? n(bestArea.composite.score) : n(bestArea.accuracy)).toFixed(1)}%` : ''}
            icon={Award} accentColor="green" />

          <StatCard title="Ultimo Simulacro"
            value={simInfo ? `${n(simInfo.overall.accuracy).toFixed(1)}%` : '--'}
            subtitle={simInfo ? simInfo.simulacro.name : 'Sin simulacros'}
            icon={Activity} accentColor="amber" />
        </div>

        {/* ======== TAB NAVIGATION ======== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'resumen', label: 'Resumen', icon: Grid3X3 },
              { id: 'grafico', label: 'Grafico', icon: BarChart3 },
              { id: 'simulacro', label: 'Simulacro', icon: Target },
              { id: 'ranking', label: 'Ranking', icon: Trophy },
              { id: 'predicciones', label: 'Predicciones', icon: Zap },
              { id: 'guia', label: 'Guia', icon: HelpCircle },
            ].map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                onClick={() => id === 'guia' ? setView('guide') : setOverviewTab(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  overviewTab === id && id !== 'guia'
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                    : darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* ---- TAB: RESUMEN ---- */}
          {overviewTab === 'resumen' && (
            <div className="space-y-6">
              {/* Priority Areas (all) with medals */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className={`text-lg font-semibold ${textPrimary}`}>Prioridades de Estudio</h2>
                    <p className={`text-xs ${textMuted} mt-0.5`}>Ordenado por impacto: preguntas esperadas RM 2026 × debilidad</p>
                  </div>
                  <Zap className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                </div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {priorityAreas.map((a, index) => {
                    const score = a.composite ? n(a.composite.score) : n(a.accuracy);
                    const color = getScoreColor(score);
                    return (
                      <motion.div key={a.area} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => fetchAreaDetail(a.area)}
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.005] hover:shadow-sm ${
                          darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                        }`}>
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          index === 0 ? 'bg-amber-400 text-amber-900' :
                          index === 1 ? 'bg-slate-300 text-slate-700' :
                          index === 2 ? 'bg-amber-600 text-white' :
                          darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                        }`}>{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{a.area}</span>
                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              {getTrendIcon(a.trend)}
                              {a.expected > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>~{a.expected}</span>}
                              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{score.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className={`h-2 rounded-full mt-1.5 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(score, 100)}%`, backgroundColor: AREA_COLORS[index % AREA_COLORS.length] }} />
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${color.light} ${color.text}`}>{color.label}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Insights Panel */}
              <div className={`rounded-2xl border ${
                darkMode
                  ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/30'
                  : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
              } p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Insights de Progreso</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      <Trophy className="h-4 w-4" /> Areas Dominadas
                    </h4>
                    <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {areas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) >= 80).length > 0 ? (
                        areas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) >= 80).slice(0, 3).map(a => (
                          <li key={a.area} className="flex items-center justify-between">
                            <span className="truncate mr-2">{a.area}</span>
                            <span className="font-semibold text-emerald-500">{(a.composite ? n(a.composite.score) : n(a.accuracy)).toFixed(0)}%</span>
                          </li>
                        ))
                      ) : (
                        <li className={`italic ${textMuted}`}>Ninguna area dominada aun</li>
                      )}
                    </ul>
                  </div>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                      <Flame className="h-4 w-4" /> Areas Criticas
                    </h4>
                    <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {priorityAreas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) < 50).length > 0 ? (
                        priorityAreas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) < 50).slice(0, 3).map(a => (
                          <li key={a.area} className="flex items-center justify-between">
                            <span className="truncate mr-2">{a.area}</span>
                            <span className="font-semibold text-rose-500">{(a.composite ? n(a.composite.score) : n(a.accuracy)).toFixed(0)}%</span>
                          </li>
                        ))
                      ) : (
                        <li className={`italic ${textMuted}`}>Ninguna area critica</li>
                      )}
                    </ul>
                  </div>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      <Info className="h-4 w-4" /> Resumen
                    </h4>
                    <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      <li className="flex justify-between"><span>Total preguntas:</span><span className="font-semibold">{summary.totalAnswers}</span></li>
                      <li className="flex justify-between"><span>Examenes dados:</span><span className="font-semibold">{summary.totalSessions}</span></li>
                      <li className="flex justify-between"><span>Dias de estudio:</span><span className="font-semibold">{summary.studyDays}</span></li>
                      <li className="flex justify-between"><span>Score global:</span><span className="font-semibold">{readinessScore.toFixed(1)}%</span></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ---- TAB: GRAFICO ---- */}
          {overviewTab === 'grafico' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-lg font-semibold ${textPrimary}`}>Perfil por Area</h2>
                  <p className={`text-xs ${textMuted} mt-0.5`}>Tu nivel actual en cada area. Click en una barra para profundizar.</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${darkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>Meta: 80%</span>
              </div>
              {barDataAreas.length > 0 ? (
                <ScoreBarChart data={barDataAreas} height={Math.max(480, barDataAreas.length * 34)} onBarClick={(payload) => fetchAreaDetail(payload.fullName)} />
              ) : (
                <div className="h-[400px] flex items-center justify-center">
                  <p className={textMuted}>Sin datos suficientes</p>
                </div>
              )}

              {/* Coach summary below chart */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/90 border border-slate-700/60' : 'bg-white border border-gray-200/70'}`}>
                  <h3 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    <TrendingUp className="h-4 w-4" /> Mejorando
                  </h3>
                  {priorityAreas.filter(a => a.trend === 'up').length > 0 ? (
                    <div className="space-y-2">
                      {priorityAreas.filter(a => a.trend === 'up').map(a => {
                        const sc = a.composite ? n(a.composite.score) : n(a.accuracy);
                        return (
                          <div key={a.area} onClick={() => fetchAreaDetail(a.area)} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                            <span className={`text-sm truncate ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{a.area}</span>
                            <span className="text-sm font-semibold text-emerald-500">{sc.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={`text-sm italic ${textMuted}`}>Sin tendencias positivas aun</p>
                  )}
                </div>
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/90 border border-slate-700/60' : 'bg-white border border-gray-200/70'}`}>
                  <h3 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                    <TrendingDown className="h-4 w-4" /> Bajando
                  </h3>
                  {priorityAreas.filter(a => a.trend === 'down').length > 0 ? (
                    <div className="space-y-2">
                      {priorityAreas.filter(a => a.trend === 'down').map(a => {
                        const sc = a.composite ? n(a.composite.score) : n(a.accuracy);
                        return (
                          <div key={a.area} onClick={() => fetchAreaDetail(a.area)} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                            <span className={`text-sm truncate ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{a.area}</span>
                            <span className="text-sm font-semibold text-rose-500">{sc.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={`text-sm italic ${textMuted}`}>Ninguna area bajando</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ---- TAB: SIMULACRO (Coach view) ---- */}
          {overviewTab === 'simulacro' && (
            <div className="space-y-6">
              {simInfo ? (
                <>
                  {/* Hero del simulacro */}
                  <div className={`relative overflow-hidden rounded-2xl p-6 ${
                    darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800/95 to-slate-900 border border-slate-700/80' : 'bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 border border-gray-200'
                  }`}>
                    <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-15 ${n(simInfo.overall.accuracy) >= 80 ? 'bg-emerald-500' : n(simInfo.overall.accuracy) >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} />
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Ultimo Simulacro</p>
                        <h2 className={`text-xl md:text-2xl font-bold mt-1 ${textPrimary} truncate`} title={simInfo.simulacro.name}>{simInfo.simulacro.name}</h2>
                        <div className="flex flex-wrap items-center gap-4 mt-2">
                          <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                            <Calendar className="w-4 h-4" />{new Date(simInfo.simulacro.completedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </span>
                          <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                            <Hash className="w-4 h-4" />{simInfo.overall.total} preguntas
                          </span>
                          <span className={`flex items-center gap-1.5 text-sm ${textSecondary}`}>
                            <Layers className="w-4 h-4" />{simInfo.simulacro.source}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-5 shrink-0">
                        <div className="relative">
                          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="42" fill="none" stroke={darkMode ? '#334155' : '#e2e8f0'} strokeWidth="8" />
                            <circle cx="50" cy="50" r="42" fill="none"
                              stroke={n(simInfo.overall.accuracy) >= 80 ? '#10b981' : n(simInfo.overall.accuracy) >= 50 ? '#f59e0b' : '#ef4444'}
                              strokeWidth="8" strokeLinecap="round"
                              strokeDasharray={`${(n(simInfo.overall.accuracy) / 100) * 264} 264`} />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className={`text-xl font-bold ${getScoreColor(n(simInfo.overall.accuracy)).text}`}>{n(simInfo.overall.accuracy).toFixed(1)}</span>
                            <span className={`text-[10px] ${textMuted}`}>score</span>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${getScoreColor(n(simInfo.overall.accuracy)).light} ${getScoreColor(n(simInfo.overall.accuracy)).text}`}>
                            {getScoreColor(n(simInfo.overall.accuracy)).label}
                          </span>
                          <p className={`text-sm ${textMuted}`}>{simInfo.overall.correct}/{simInfo.overall.total} correctas</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3 stat cards del simulacro */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard title="Correctas" value={simInfo.overall.correct} subtitle={`de ${simInfo.overall.total} preguntas`} icon={CheckCircle} accentColor="green" />
                    <StatCard title="Incorrectas" value={simInfo.overall.total - simInfo.overall.correct - (simInfo.overall.skipped || 0)} subtitle="Revisa estos temas" icon={AlertTriangle} accentColor="rose" />
                    <StatCard title="Areas Evaluadas" value={(simInfo.areas || []).length} subtitle={`${(simInfo.areas || []).filter(a => n(a.accuracy) >= 80).length} dominadas`} icon={Layers} accentColor="purple" />
                  </div>

                  {/* Desglose por area con barras de colores */}
                  <div>
                    <h2 className={`text-lg font-semibold mb-3 ${textPrimary}`}>Desglose por Area</h2>
                    <div className="space-y-2.5">
                      {(simInfo.areas || []).sort((a, b) => n(a.accuracy) - n(b.accuracy)).map((area, i) => {
                        const acc = n(area.accuracy);
                        const color = getScoreColor(acc);
                        return (
                          <motion.div key={area.area || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => fetchAreaDetail(area.area)}
                            className={`p-3.5 rounded-xl cursor-pointer transition-all hover:scale-[1.005] ${
                              darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                            }`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                                  i === 0 && acc < 50 ? 'bg-rose-500 text-white' :
                                  acc >= 80 ? (darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700') :
                                  darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                                }`}>{i + 1}</span>
                                <span className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{area.area}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-xs ${textMuted}`}>{area.correct}/{area.total}</span>
                                <span className={`text-sm font-bold ${color.text}`}>{acc.toFixed(0)}%</span>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>{color.label}</span>
                              </div>
                            </div>
                            <div className={`h-2.5 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(acc, 100)}%`, backgroundColor: AREA_COLORS[i % AREA_COLORS.length] }} />
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Coach analysis */}
                  <div className={`rounded-2xl border p-5 ${
                    darkMode ? 'bg-gradient-to-br from-amber-900/20 to-orange-900/20 border-amber-500/30' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Lightbulb className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <h3 className={`text-lg font-bold ${textPrimary}`}>Analisis del Coach</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                        <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                          <Flame className="h-4 w-4" /> Donde Perdiste Puntos
                        </h4>
                        <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {(simInfo.areas || []).filter(a => n(a.accuracy) < 60).sort((a, b) => n(a.accuracy) - n(b.accuracy)).slice(0, 5).map(a => (
                            <li key={a.area} className="flex items-center justify-between">
                              <span className="truncate mr-2">{a.area}</span>
                              <span className="font-bold text-rose-400">{a.correct}/{a.total} ({n(a.accuracy).toFixed(0)}%)</span>
                            </li>
                          ))}
                          {(simInfo.areas || []).filter(a => n(a.accuracy) < 60).length === 0 && (
                            <li className={`italic ${textMuted}`}>Todas las areas sobre 60%</li>
                          )}
                        </ul>
                      </div>
                      <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                        <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                          <CheckCircle className="h-4 w-4" /> Donde Brillaste
                        </h4>
                        <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {(simInfo.areas || []).filter(a => n(a.accuracy) >= 80).sort((a, b) => n(b.accuracy) - n(a.accuracy)).slice(0, 5).map(a => (
                            <li key={a.area} className="flex items-center justify-between">
                              <span className="truncate mr-2">{a.area}</span>
                              <span className="font-bold text-emerald-400">{a.correct}/{a.total} ({n(a.accuracy).toFixed(0)}%)</span>
                            </li>
                          ))}
                          {(simInfo.areas || []).filter(a => n(a.accuracy) >= 80).length === 0 && (
                            <li className={`italic ${textMuted}`}>Ninguna area sobre 80% aun</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-16 flex flex-col items-center justify-center gap-4">
                  <Target className={`w-16 h-16 ${textMuted}`} />
                  <h3 className={`text-xl font-semibold ${textPrimary}`}>Sin simulacros completados</h3>
                  <p className={`text-sm ${textSecondary} max-w-md text-center`}>Completa tu primer simulacro para ver un analisis detallado de tu rendimiento por area, con recomendaciones personalizadas.</p>
                </div>
              )}
            </div>
          )}

          {/* ---- TAB: RANKING ---- */}
          {overviewTab === 'ranking' && (
            <div className="space-y-6">
              {/* All area cards */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className={`text-lg font-semibold ${textPrimary}`}>Todas las Areas por Impacto</h2>
                  <span className={`text-xs ${textMuted}`}>{priorityAreas.length} areas evaluadas</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {priorityAreas.map((a, index) => {
                    const score = a.composite ? n(a.composite.score) : n(a.accuracy);
                    const color = getScoreColor(score);
                    return (
                      <motion.div key={a.area} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}
                        onClick={() => fetchAreaDetail(a.area)}
                        className={`${darkMode ? 'bg-slate-800/90 border-slate-700/60' : 'bg-white border-gray-200/70'} border rounded-2xl p-5 shadow-lg cursor-pointer hover:scale-[1.02] hover:shadow-xl transition-all duration-200`}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`font-semibold text-sm ${textPrimary} truncate`}>{a.area}</h3>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            index === 0 ? 'bg-amber-400 text-amber-900' :
                            index === 1 ? 'bg-slate-300 text-slate-700' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                          }`}>{index + 1}</span>
                        </div>
                        <div className="flex items-end gap-3 mb-2">
                          <p className={`text-2xl font-bold ${color.text}`}>{score.toFixed(1)}</p>
                          {a.expected > 0 && <span className={`text-xs ${textMuted} mb-1`}>~{a.expected} esperadas</span>}
                        </div>
                        <div className={`h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(score, 100)}%`, backgroundColor: AREA_COLORS[index % AREA_COLORS.length] }} />
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className={`text-xs ${textMuted}`}>{a.total} preg.</span>
                          <div className="flex items-center gap-1.5">
                            {getTrendIcon(a.trend)}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>{color.label}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ---- TAB: PREDICCIONES ---- */}
          {overviewTab === 'predicciones' && (
            <div className="space-y-6">
              <div className={`rounded-2xl p-5 ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200'}`}>
                <div className="flex items-center gap-2 mb-5">
                  <Lightbulb className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <h2 className={`text-lg font-bold ${textPrimary}`}>Recomendaciones de Estudio Dirigido</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h3 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                      <Flame className="h-4 w-4" /> Areas Criticas
                    </h3>
                    {priorityAreas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) < 50).length > 0 ? (
                      <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {priorityAreas.filter(a => (a.composite ? n(a.composite.score) : n(a.accuracy)) < 50).slice(0, 5).map(a => {
                          const sc = a.composite ? n(a.composite.score) : n(a.accuracy);
                          return (
                            <li key={a.area} className="flex items-center justify-between">
                              <span className="truncate mr-2" title={a.area}>{a.area}</span>
                              <span className="font-bold text-rose-400">{sc.toFixed(0)}%</span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className={`text-sm italic ${textMuted}`}>Ninguna area bajo 50%</p>
                    )}
                  </div>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h3 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      <TrendingUp className="h-4 w-4" /> Mejorando
                    </h3>
                    {priorityAreas.filter(a => a.trend === 'up').length > 0 ? (
                      <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {priorityAreas.filter(a => a.trend === 'up').slice(0, 5).map(a => (
                          <li key={a.area} className="flex items-center gap-2">
                            <TrendingUp className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                            <span className="truncate" title={a.area}>{a.area}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`text-sm italic ${textMuted}`}>Sin tendencias positivas aun</p>
                    )}
                  </div>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <h3 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      <Zap className="h-4 w-4" /> Mayor Impacto RM 2026
                    </h3>
                    {predictionsData?.areas ? (
                      <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {priorityAreas.slice(0, 5).map(a => (
                          <li key={a.area} className="flex items-center justify-between">
                            <span className="truncate mr-2" title={a.area}>{a.area}</span>
                            <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>~{a.expected} preg.</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`text-sm italic ${textMuted}`}>Cargando predicciones...</p>
                    )}
                  </div>
                </div>
              </div>

              {/* All area cards */}
              <div>
                <h2 className={`text-lg font-semibold mb-3 ${textPrimary}`}>Todas las Areas por Impacto</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {priorityAreas.map((a, index) => {
                    const score = a.composite ? n(a.composite.score) : n(a.accuracy);
                    const color = getScoreColor(score);
                    return (
                      <motion.div key={a.area} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}
                        onClick={() => fetchAreaDetail(a.area)}
                        className={`${darkMode ? 'bg-slate-800/90 border-slate-700/60' : 'bg-white border-gray-200/70'} border rounded-2xl p-5 shadow-lg cursor-pointer hover:scale-[1.02] hover:shadow-xl transition-all duration-200`}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`font-semibold text-sm ${textPrimary} truncate`}>{a.area}</h3>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            index === 0 ? 'bg-amber-400 text-amber-900' :
                            index === 1 ? 'bg-slate-300 text-slate-700' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                          }`}>{index + 1}</span>
                        </div>
                        <div className="flex items-end gap-3 mb-2">
                          <p className={`text-2xl font-bold ${color.text}`}>{score.toFixed(1)}</p>
                          {a.expected > 0 && <span className={`text-xs ${textMuted} mb-1`}>~{a.expected} esperadas</span>}
                        </div>
                        <div className={`h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(score, 100)}%`, backgroundColor: AREA_COLORS[index % AREA_COLORS.length] }} />
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className={`text-xs ${textMuted}`}>{a.total} preg.</span>
                          <div className="flex items-center gap-1.5">
                            {getTrendIcon(a.trend)}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>{color.label}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* unevaluated areas (outside tabs) */}
        {unevaluatedAreas && unevaluatedAreas.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <button onClick={() => setShowUnevaluated(!showUnevaluated)}
              className={`flex items-center gap-2 text-sm font-medium ${textSecondary} hover:${darkMode ? 'text-white' : 'text-slate-800'} transition-colors`}>
              {showUnevaluated ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>Areas sin evaluar ({unevaluatedAreas.length})</span>
              {showUnevaluated ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showUnevaluated && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 flex flex-wrap gap-2">
                {unevaluatedAreas.map((a) => (
                  <span key={a} className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-slate-600'}`}>{a}</span>
                ))}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ProgressDashboard;
